import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Divider,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography
} from '@material-ui/core';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Loader } from '../elements';
import { LoaderSize } from '../constants/constants';
import { HealthSystemDomainUsersResultUserViewModel } from '../api/HealthSystemApi';
import { ApiService } from '../hooks';
import { notifications, extractApiErrorMessage } from '../utils';
import EmployeeAvatar from './EmployeeAvatar';

const VISIBLE_AVATARS = 2;

interface IProps {
  selectedEmployees: HealthSystemDomainUsersResultUserViewModel[];
  onChange: (employees: HealthSystemDomainUsersResultUserViewModel[]) => void;
  disabled?: boolean;
}

const EmployeeSelectModal: React.FC<IProps> = ({ selectedEmployees, onChange, disabled }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [open, setOpen] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [allEmployees, setAllEmployees] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const [tempSelected, setTempSelected] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);

  const loadEmployees = () => {
    setIsBusy(true);
    apiService.api.users
      .usersGetUsersAll({})
      .then((result) => {
        setAllEmployees(result.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  React.useEffect(() => {
    if (open && !loaded) loadEmployees();
  }, [open]);

  const handleOpen = () => {
    setTempSelected([...selectedEmployees]);
    setSearchText('');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleApply = () => {
    onChange(tempSelected);
    setOpen(false);
  };

  const handleToggle = (employee: HealthSystemDomainUsersResultUserViewModel) => {
    const already = tempSelected.some((e) => e.id === employee.id);
    setTempSelected(already ? tempSelected.filter((e) => e.id !== employee.id) : [...tempSelected, employee]);
  };

  const filteredEmployees = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return q
      ? allEmployees.filter((e) => e.name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q))
      : allEmployees;
  }, [allEmployees, searchText]);

  const handleSelectAll = () => setTempSelected([...filteredEmployees]);
  const handleClearAll = () => setTempSelected([]);
  const isSelected = (id: string) => tempSelected.some((e) => e.id === id);

  const renderTriggerContent = () => {
    if (selectedEmployees.length === 0) {
      return (
        <div className="flex items-center justify-between w-full">
          <span className="text-gray-500 text-sm">{t('f-employee')}</span>
          <Users size={16} className="text-gray-400" />
        </div>
      );
    }

    const visible = selectedEmployees.slice(0, VISIBLE_AVATARS);
    const remaining = selectedEmployees.length - VISIBLE_AVATARS;

    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          {/* Overlapping avatar stack */}
          <div className="flex -space-x-2">
            {visible.map((emp) => (
              <EmployeeAvatar key={emp.id} name={emp.name} size="sm" className="ring-2 ring-white" />
            ))}
            {remaining > 0 && (
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 ring-2 ring-white shrink-0">
                {remaining > 9 ? '9+' : `+${remaining}`}
              </div>
            )}
          </div>
          <span className="text-sm text-gray-700">
            {selectedEmployees.length} {t('f-com-selected')}
          </span>
        </div>
        <Users size={16} className="text-gray-400" />
      </div>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        disabled={disabled}
        onClick={handleOpen}
        fullWidth
        style={{ justifyContent: 'flex-start', textTransform: 'none', padding: '6px 12px' }}
      >
        {renderTriggerContent()}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          disableTypography
          style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}
        >
          <div className="flex items-center gap-2">
            <Users size={20} className="text-blue-600" />
            <span className="text-base font-semibold text-gray-800">{t('f-employee')}</span>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: '16px 24px', minHeight: 400, overflowX: 'hidden' }}>
          {/* Search */}
          <Box mb={2}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={t('f-com-search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span style={{ display: 'flex', color: '#9e9e9e' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </span>
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')} edge="end">
                      <span style={{ display: 'flex', color: '#9e9e9e' }}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </span>
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>

          {/* Select all / Clear all — matches SlaughterhouseSelectModal style */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="textSecondary">
              {tempSelected.length} {t('f-com-selected')}
            </Typography>
            <Box>
              <Button size="small" color="primary" onClick={handleSelectAll} disabled={!loaded || isBusy}>
                {t('f-com-select-all')}
              </Button>
              <Button
                size="small"
                color="secondary"
                onClick={handleClearAll}
                disabled={tempSelected.length === 0}
                style={{ marginLeft: 8 }}
              >
                {t('f-com-clear-all')}
              </Button>
            </Box>
          </Box>

          <Divider />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <List disablePadding style={{ maxHeight: 320, overflowY: 'auto', marginTop: 4 }}>
              {filteredEmployees.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                  <Users size={32} />
                  <span className="text-sm">{t('f-no-data')}</span>
                </div>
              )}

              {filteredEmployees.map((emp) => {
                const selected = isSelected(emp.id);
                return (
                  <ListItem
                    key={emp.id}
                    button
                    onClick={() => handleToggle(emp)}
                    style={{
                      borderRadius: 8,
                      marginBottom: 4,
                      border: selected ? '1.5px solid #bfdbfe' : '1.5px solid transparent',
                      backgroundColor: selected ? '#eff6ff' : undefined
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <EmployeeAvatar name={emp.name} size="md" />
                      <ListItemText
                        primary={
                          <span className={`text-sm font-medium ${selected ? 'text-blue-800' : 'text-gray-800'}`}>
                            {emp.name}
                          </span>
                        }
                        secondary={emp.email && <span className="text-xs text-gray-500">{emp.email}</span>}
                      />
                    </div>
                    <ListItemSecondaryAction>
                      <Checkbox edge="end" checked={selected} onChange={() => handleToggle(emp)} color="primary" />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

        <DialogActions style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={handleClose} color="default">
            {t('f-com-cancel')}
          </Button>
          <Button onClick={handleApply} color="primary" variant="contained" disabled={isBusy}>
            {t('f-com-apply')} ({tempSelected.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmployeeSelectModal;
